
import type { Selection } from 'd3-selection'
import type { Transition } from 'd3-transition'
import type { ZoomBehavior } from 'd3-zoom'
import { Component, Prop, Vue } from 'vue-property-decorator'

import { accessibleTicketGroupColor } from '@tix/themes/helpers'

import { indexBy, indexItemsById } from '@/helpers/IndexHelpers'
import { getAccessibilityType } from '@/helpers/TicketGroupHelpers'
import { getTicketGroupColor } from '@/helpers/TicketGroupsColors'

import type { AccessibilityType, LinkedTG } from '@/api/types/processedEntities'
import ConfirmModal from '@/modals/ConfirmModal.vue'
import { openModal } from '@/modals/helpers/api'
import NeighboringSeatQueue from '@/seats/NeighboringSeatQueue'
import type Seat from '@/seats/Seat'
import type { AssignedSeat } from '@/seats/assignments'
import { ttToTGQuantities } from '@/seats/assignments'
import { TTQuantities, initializeSeats } from '@/seats/helpers'

const maxZoom = 8
const minZoom = 1

type ConfirmRequiredAccessibilityType = Extract<AccessibilityType, 'wheelchair' | 'companion'>

@Component({ name: 'SeatMap' })
export default class SeatMap extends Vue {
  @Prop({ required: true })
  mapData: MapGeometry | null

  @Prop({ required: true })
  seatData: SeatEntity[]

  @Prop({ required: true })
  groups: LinkedTG[]

  @Prop({ required: true })
  filter: string | null

  @Prop({ required: true })
  quantities: TTQuantities

  @Prop({ required: true })
  value: AssignedSeat[] | null

  seats: Seat[] = []

  focusedSeatName: string | null = null

  zoomBehavior: ZoomBehavior<Element, unknown> | null = null

  view: Selection<Element, unknown, any, any> | null = null
  currentZoom: number = 0

  d3: any = {}
  hoveredSeat: Seat | null = null
  assignedSeats: AssignedSeat[] | null = null
  pieKeys: string[]

  created() {
    // Asynchronously load d3 imports
    import('@/seats/D3').then(({ select, zoom, zoomTransform }) => {
      this.d3.select = select
      this.d3.zoom = zoom
      this.d3.zoomTransform = zoomTransform
      this.onLoad()
    })

    this.initializeSeats()
  }

  mounted() {
    this.$nextTick(() => {
      this.refocusSeat()
    })
  }

  initializeSeats() {
    // The order here is critical.
    this.seats = initializeSeats(this.seatData, ttToTGQuantities(this.quantities, this.groups))

    // Update the accessibility type for each seat.
    for (const [index, seat] of this.seats.entries()) {
      seat.index = index
      const accessibleGroup = Array.from(seat.tgSet).find((tgId) => this.accessibilityByTgId.has(tgId))
      if (accessibleGroup) {
        const tg = this.groups.find((tg) => tg.id === accessibleGroup)
        if (tg) {
          seat.accessibilityType = getAccessibilityType(tg)
        }
      }
    }

    if (this.seats.length > 0) {
      this.seats[this.seats.length - 1].isLast = true
    }

    // Initialize the focused seat to the first available seat.
    this.focusedSeatName = this.seats[0]?.name ?? null

    this.pieKeys = this.getPieKeys()
    if (this.value) {
      this.assignedSeats = this.restoreSeatSelection(this.value) ?? null
      this.$forceUpdate()
    }
  }

  private restoreSeatSelection(oldSelection: AssignedSeat[]): AssignedSeat[] | void {
    // Index the new Seat instances by their name.
    const index = indexBy('name', this.seats)

    // Iterate the old selected Seat instances.
    for (const old of oldSelection) {
      // Find the new Seat instance by its name and select it.
      const seat = index[old.seat.name]
      if (seat.state === 'available') {
        const result = seat.select()
        if (result) {
          // The selection is complete. Quantities were probably reduced since the seats were last selected.
          return result
        }
      }
    }
  }

  get pieCharts(): Dictionary {
    const result = {}

    if (this.pieKeys) {
      const groups = indexItemsById(this.groups)
      // Size of the raster file that will be generated
      // Double the radius to get the diameter, multiply to support zoom, double again to support retina
      const size = this.largestSeatRadius * 2 * maxZoom * 2
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')!
      context.canvas.width = size
      context.canvas.height = size

      for (const chart of this.pieKeys) {
        context.clearRect(0, 0, size, size)
        const tgIDs = chart.split(',')
        // A start angle of 0 would start drawing at the 3 o'clock position, this sets the start angle to 6 o'clock position
        let startAngle = Math.PI * 0.5
        // Radian units are used to draw arcs on Canvas. A full circle is 2 * PI radians
        const sliceSize = (2 * Math.PI) / tgIDs.length

        // Use ADA blue for entire pie if any TG is accessible
        const hasAnyAccessible = tgIDs.some((id) => groups[id]?.isAccessible)

        for (const tgID of tgIDs) {
          const tg = groups[tgID]
          let color = getTicketGroupColor(tg)
          if (hasAnyAccessible) {
            color = accessibleTicketGroupColor
          }
          const endAngle = startAngle + sliceSize
          this.drawSegment(context, startAngle, endAngle, color)
          startAngle = endAngle
        }

        result[chart] = canvas.toDataURL()
      }
    }

    return result
  }

  get accessibilityByTgId(): Map<string, AccessibilityType> {
    const result = new Map<string, AccessibilityType>()
    for (const group of this.groups) {
      if (group.isAccessible) {
        const accessibilityType = getAccessibilityType(group)
        if (accessibilityType) {
          result.set(group.id, accessibilityType)
        }
      }
    }
    return result
  }

  get shouldShowAccessibilityIcons(): boolean {
    if (!this.filter) return true
    return this.accessibilityByTgId.has(this.filter)
  }

  getSeatAccessibilityType(seat: Seat): AccessibilityType | null {
    for (const tgId of seat.tgSet) {
      const type = this.accessibilityByTgId.get(tgId)
      if (type) return type
    }
    return null
  }

  getAccessibilityIconSize(seat: Seat): { width: number; height: number; x: number; y: number } {
    const iconScale = 0.8
    const iconHeight = seat.radius * 2 * iconScale
    const iconWidth = iconHeight * 0.8
    return {
      width: iconWidth,
      height: iconHeight,
      x: seat.x - iconWidth / 2,
      y: seat.y - iconHeight / 2,
    }
  }

  get largestSeatRadius(): number {
    const radii = this.seats.map((seat) => seat.radius)
    return Math.max(...radii)
  }

  drawSegment(ctx, start, end, color) {
    const center = ctx.canvas.width / 2
    ctx.fillStyle = color
    ctx.beginPath()
    ctx.moveTo(center, center)
    ctx.arc(center, center, center, start, end)
    ctx.closePath()
    ctx.fill()
  }

  onLoad() {
    if (this.$refs.map) {
      // Initialize d3 view
      this.view = this.d3.select('svg.map')

      // Initialize zoom behavior
      this.zoomBehavior = this.d3.zoom().on('zoom', this.onZoom)

      if (!this.view || !this.zoomBehavior) return

      // Zoom min/max levels
      this.zoomBehavior.scaleExtent([minZoom, maxZoom])

      // Drag min/max levels
      const margin = 50
      const worldEdge: [[number, number], [number, number]] = [
        [-margin, -margin],
        [this.mapData!.width + margin, this.mapData!.height + margin],
      ]
      this.zoomBehavior.translateExtent(worldEdge)

      this.view.call(this.zoomBehavior)

      this.view.on('dblclick.zoom', null)
    } else {
      throw new Error('Seat map element not found')
    }
  }

  getPieKeys(): string[] {
    // Loop active seats and find which color combinations are used
    const result: Set<string> = new Set()

    for (const { pieKey } of this.seats) {
      result.add(pieKey)
    }

    // Ignore unavailable pieKeys
    result.delete('unavailable')

    return Array.from(result)
  }

  onZoom({ transform }) {
    // d3 stores the current zoom level as 'k'.

    this.currentZoom = this.d3.zoomTransform(this.view!.node() as any).k
    this.view!.select('g').attr('transform', transform)
  }

  onSeatClick(seat: Seat) {
    this.focusedSeatName = seat.name

    this.hoveredSeat = null
    // Ignore clicks on booked seats.
    // Booked seats use the same template element (`<image>`) as available seats (but with reduced opacity).
    if (seat.state === 'available') {
      const accessibilityType = this.getSeatAccessibilityType(seat)
      if (accessibilityType === 'wheelchair') {
        this.showAccessibilityConfirmation(seat, accessibilityType)
      } else if (accessibilityType === 'companion' && !this.hasSelectedPrimaryAccessibleSeat()) {
        this.showAccessibilityConfirmation(seat, accessibilityType)
      } else {
        this.assignedSeats = this.automaticallySelectNeighbouringSeats(seat) ?? null
        this.$forceUpdate()
        this.$nextTick(() => {
          this.refocusSeat()
        })
      }
    }
  }

  private focusNextAvailableSeat(seat: Seat) {
    const nextSeat = this.seats.find((s) => s.index > seat.index && s.state === 'available')?.name
    if (nextSeat) {
      this.focusedSeatName = nextSeat
      this.refocusSeat()
    }
  }

  private focusPreviousAvailableSeat(seat: Seat) {
    const prevSeat = [...this.seats].reverse().find((s) => s.index < seat.index && s.state === 'available')?.name
    if (prevSeat) {
      this.focusedSeatName = prevSeat
      this.refocusSeat()
    }
  }

  handleSeatKeyEvent(e: KeyboardEvent, seat: Seat) {
    let direction = 0

    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      direction = 1
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      direction = -1
    }

    if (direction !== 0) {
      const nextIndex = (seat.index ?? 0) + direction
      const nextSeat = this.seats.find((s) => s.index === nextIndex)
      if (nextSeat) {
        e.preventDefault()
        this.focusedSeatName = nextSeat?.name ?? null
        this.refocusSeat()
      }
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()

      // Only allow selection of available seats that are selectable (i.e. have a pie chart)
      if (seat.state === 'available' && this.showPieChart(seat)) {
        this.onSeatClick(seat)
      } else if (seat.state === 'selected') {
        this.deselectSeat(seat)
      }
    } else if (e.key === 'n') {
      e.preventDefault()
      this.focusNextAvailableSeat(seat)
    } else if (e.key === 'p') {
      e.preventDefault()
      this.focusPreviousAvailableSeat(seat)
    }
  }

  private hasSelectedPrimaryAccessibleSeat(): boolean {
    for (const seat of this.seats) {
      if (seat.state === 'selected') {
        const type = this.getSeatAccessibilityType(seat)
        return type === 'wheelchair' || type === 'semi-ambulatory'
      }
    }
    return false
  }

  private showAccessibilityConfirmation(seat: Seat, accessibilityType: ConfirmRequiredAccessibilityType) {
    const accessibilityLabels: Record<ConfirmRequiredAccessibilityType, string> = {
      wheelchair: 'This seat is reserved for wheelchair users.',
      companion:
        'This seat is for wheelchair companions only.  It must be purchased in conjunction with a wheelchair seat.',
    }

    const message = accessibilityLabels[accessibilityType] + ' Are you sure you want to select this seat?'

    openModal({
      name: 'accessible-seat-confirm-modal',
      component: ConfirmModal,
      title: 'Accessible Seat',
      size: 'sm',
      props: { message },
      describedBy: 'confirm-modal-message',
      onClose: (data) => {
        if (data?.confirmed) {
          this.assignedSeats = this.automaticallySelectNeighbouringSeats(seat) ?? null
          this.$forceUpdate()
        }

        this.$nextTick(() => {
          this.refocusSeat()
        })
      },
    })
  }

  private automaticallySelectNeighbouringSeats(first: Seat): AssignedSeat[] | void {
    const queue = new NeighboringSeatQueue()

    let seat: Seat | undefined = first
    while (seat) {
      queue.add(seat.next)
      queue.add(seat.prev)
      // console.log('Selecting', seat.name)
      // console.log('Queue', queue.items.map(s => s.name))
      const result = seat.select()
      if (result) {
        // Selection is complete.
        return result
      }
      seat = queue.next
    }
  }

  deselectSeat(seat: Seat) {
    // Rerender when a seat is deselected.
    // It is too slow to watch all the Seat instances on maps with more than 1000 seats.
    this.$forceUpdate()
    seat.deselect()
    this.assignedSeats = null

    this.focusedSeatName = seat.name
    this.$nextTick(() => {
      this.refocusSeat()
    })
  }

  adjustZoom(amount) {
    // Use the `Transition` type so that TypeScript imports @types/d3-transition and discovers
    // additional methods on d3-selection's `Selection` type.

    const transition: Transition<Element, unknown, any, any> = this.view!.transition()
    transition.call(this.zoomBehavior!.scaleBy, amount)
  }

  zoomIn() {
    this.adjustZoom(2)
  }

  zoomOut() {
    this.adjustZoom(0.75)
  }

  get canZoomIn() {
    return this.currentZoom < maxZoom
  }

  get canZoomOut() {
    return this.currentZoom > minZoom
  }

  get background(): string {
    return `<g>${this.mapData!.other_elements}</g>`
  }

  get mapLegend() {
    // Select the pattern with the most colors
    const sortedGroups = this.pieKeys!.sort((a, b) => b.split(',').length - a.split(',').length)
    const exampleChart = this.pieCharts[sortedGroups[0]]

    return [
      {
        className: 'available',
        label: 'Available',
        image: exampleChart,
      },
      {
        className: 'booked',
        label: 'Sold out',
        image: exampleChart,
      },
      {
        className: 'unavailable',
        label: 'Unavailable',
      },
      {
        className: 'selected',
        label: 'Selected',
      },
    ]
  }

  get seatRadius() {
    // Currently this only supports maps where all seats have the same radius
    return this.seats[0].radius
  }

  get seatDiameter() {
    return this.seatRadius * 2
  }

  onMouseEnter(seat) {
    if (seat.state === 'available') {
      this.hoveredSeat = seat
    }
  }

  onMouseLeave() {
    this.hoveredSeat = null
  }

  confirmSelection() {
    if (!this.assignedSeats) {
      this.$emit('invalid')
    } else {
      this.$emit('input', [...this.assignedSeats!])
    }
  }

  showPieChart(seat: Seat): boolean {
    if (seat.state === 'available' || seat.state === 'booked') {
      if (this.filter) {
        return seat.tgSet.has(this.filter)
      } else {
        return true
      }
    } else {
      return false
    }
  }

  getPieDataUrl(seat: Seat): string {
    const key = this.filter ? this.filter : seat.pieKey
    return this.pieCharts[key]
  }

  getTickPath(seat: Seat): string {
    const r = seat.radius * 0.5
    const x = seat.x
    const y = seat.y
    return `M ${x - r * 0.6} ${y} L ${x - r * 0.1} ${y + r * 0.5} L ${x + r * 0.7} ${y - r * 0.4}`
  }

  getCrossPath(seat: Seat): string {
    const r = seat.radius * 0.45
    const x = seat.x
    const y = seat.y
    return `M ${x - r} ${y - r} L ${x + r} ${y + r} M ${x + r} ${y - r} L ${x - r} ${y + r}`
  }

  refocusSeat() {
    const focusedSeat = this.seats.find((s) => s.name === this.focusedSeatName)
    if (focusedSeat) {
      const seat = this.$el.querySelector(`[data-seat-index="${focusedSeat.index}"]`) as HTMLElement | null
      seat?.focus()
    }
  }
}
